import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";

const API_ORIGIN = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";
const API_V1 = `${API_ORIGIN}/api/v2`;

export default function Register() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: "",
    dateOfBirth: "",
    lastName: "",
    email: "",
    country: "CA",
    password: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  function onChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Basic client validation
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError("Please enter your first and last name.");
      return;
    }
    if (!form.email.trim()) {
      setError("Please enter your email.");
      return;
    }
    if (!form.password || form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!agreedToTerms) {
      setError("Please agree to the Terms of Service and Privacy Policy to continue.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        dateOfBirth: form.dateOfBirth,
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        country: form.country || "CA",
      };

      const res = await fetch(`${API_V1}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        // ignore
      }

      if (!res.ok) {
        const msg = data?.error || data?.message || `Registration failed (${res.status})`;
        throw new Error(msg);
      }

      if (data?.data?.token) {
        localStorage.setItem("token", data.data.token);
        localStorage.setItem("user", JSON.stringify(data.data.user));
      }
      setSuccess(data?.message || "Account created successfully! Redirecting...");
      setTimeout(() => navigate("/verify-phone"), 800);
    } catch (err) {
      setError(err.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 py-8 px-4">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <img src="/logo.png" alt="ZoomPay" style={{height: "90px", width: "auto", margin: "0 auto"}} />
          <p className="text-blue-300 text-sm mt-2">Send Money to Africa, Instantly</p>
        </div>
      <div className="bg-white rounded-2xl shadow-xl p-8">
      <h1 className="text-2xl font-bold mb-1 text-center text-slate-900">Create Your ZoomPay Account</h1>
      <p className="text-center text-gray-500 text-sm mb-6">Join thousands sending money to Africa</p>

      {error && (
        <div className="p-3 mb-4 rounded bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 mb-4 rounded bg-green-50 text-green-700 border border-green-200">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold mb-1">First Name *</label>
            <input
              name="firstName"
              value={form.firstName}
              onChange={onChange}
              className="w-full border rounded p-2"
              placeholder="First name"
              required
            />
          </div>

          <div>
            <label className="block font-semibold mb-1">Last Name *</label>
            <input
              name="lastName"
              value={form.lastName}
              onChange={onChange}
              className="w-full border rounded p-2"
              placeholder="Last name"
              required
            />
          </div>
        </div>

        <div>
          <label className="block font-semibold mb-1">Email *</label>
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={onChange}
            className="w-full border rounded p-2"
            placeholder="your@email.com"
            required
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Date of Birth *
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={form.dateOfBirth}
                  onChange={e => setForm({...form, dateOfBirth: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
          <select
            name="country"
            value={form.country}
            onChange={onChange}
            className="w-full border rounded p-2"
          >
            <option value="CA">Canada</option>
            <option value="US">United States</option>
            <option value="KE">Kenya</option>
          </select>
        </div>

        <div className="mt-6">
          <h2 className="text-xl font-bold mb-3">Security</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-semibold mb-1">Password *</label>
              <input
                name="password"
                type="password"
                value={form.password}
                onChange={onChange}
                className="w-full border rounded p-2"
                placeholder="Repeat password"
                required
              />
              <p className="text-sm text-gray-500 mt-1">Must be at least 8 characters</p>
            </div>

            <div>
              <label className="block font-semibold mb-1">Confirm Password *</label>
              <input
                name="confirmPassword"
                type="password"
                value={form.confirmPassword}
                onChange={onChange}
                className="w-full border rounded p-2"
                placeholder="Repeat password"
                required
              />
            </div>
          </div>
        </div>


        {/* Terms & Privacy Policy */}
        <div className="flex items-start gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <input
            type="checkbox"
            id="agreeTerms"
            checked={agreedToTerms}
            onChange={e => setAgreedToTerms(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-blue-600 cursor-pointer shrink-0"
          />
          <label htmlFor="agreeTerms" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
            I agree to ZoomPay's{" "}
            <a href="/terms" className="text-blue-600 hover:underline font-medium">Terms of Service</a>{" "}and{" "}
            <a href="/privacy" className="text-blue-600 hover:underline font-medium">Privacy Policy</a>.
            By creating an account I confirm I am 18+ and a resident of Canada or the USA.
          </label>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white p-3 rounded font-semibold hover:bg-blue-700 disabled:opacity-60"
        >
          {loading ? "Creating Account..." : "Create Account"}
        </button>

        <p className="text-center text-sm mt-3">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-600 font-semibold">
            Sign in here
          </Link>
        </p>

        <p className="text-center text-xs text-gray-500 mt-2">
          API: {API_V1}
        </p>
      </form>
      </div>
      <div className="text-center mt-6">
        <p className="text-blue-300 text-sm">Already have an account? <a href="/login" className="text-amber-400 font-semibold hover:text-amber-300">Sign In</a></p>
        <p className="text-blue-400 text-xs mt-3">FINTRAC MSB C10001588 · ZoomPay Systems & Technology Ltd.</p>
      </div>
      </div>
    </div>
  );
}